import { Routes, Route, Navigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import AuthPage from './pages/Auth/AuthPage';
import { SingleQuizPage } from './pages/Quiz/SingleQuizPage';
import { ResultPage } from './pages/Result/ResultPage';
import { StatsPage } from './pages/Stats/StatsPage';
import { RankingPage } from './pages/Ranking/RankingPage';
import { WrongBookPage } from './pages/WrongBook/WrongBookPage';
import { SurveyPage } from './pages/Survey/SurveyPage';
import { MePage } from './pages/Me/MePage';
import { CertPage } from './pages/Cert/CertPage';
import { Icon } from './components/Icon';
import { authedRequest } from './api/client';

interface WxConfig {
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
}

interface WxSub {
  config: (opts: Record<string, unknown>) => void;
  ready: (cb: () => void) => void;
  error: (cb: (res: unknown) => void) => void;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('emp_token');
  const location = useLocation();
  if (!token) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

function HomePage() {
  const empCode = localStorage.getItem('emp_code') || '';
  const [searchParams] = useSearchParams();
  const [subscribed, setSubscribed] = useState(false);
  // 订阅按钮初始化阶段：loading=拉取签名中 / ready=开放标签已注入 / fail=失败（附原因）
  const [subState, setSubState] = useState<{ phase: 'loading' | 'ready' | 'fail'; msg?: string }>({ phase: 'loading' });
  const isWx = /micromessenger/i.test(navigator.userAgent);
  const subRef = useRef<HTMLDivElement | null>(null);

  // 微信内：初始化 wx.config 并注入 wx-open-subscribe 订阅按钮（新版订阅通知）
  useEffect(() => {
    if (!isWx) return;

    // 从微信订阅授权页跳回时，URL 带 ?subscribed=remind（兼容旧回跳）
    if (searchParams.get('subscribed') === 'remind') {
      setSubscribed(true);
      const t = setTimeout(() => setSubscribed(false), 4000);
      return () => clearTimeout(t);
    }

    let cancelled = false;

    // wx.config 成功但开放标签仍不可用时，微信会在 document 上触发该事件，
    // e.detail.errMsg 给出具体原因（如版本过低 / 未在安全域名下 / 标签权限被回收）
    const onOpenTagsError = (e: Event) => {
      const detail = (e as CustomEvent<{ errMsg?: string }>).detail;
      const msg = detail?.errMsg || '未知原因';
      console.error('[WeixinOpenTagsError]', detail);
      setSubState({ phase: 'fail', msg: `微信订阅组件不可用：${msg}` });
    };
    document.addEventListener('WeixinOpenTagsError', onOpenTagsError);

    const wx = (window as unknown as { wx?: WxSub }).wx;
    if (!wx) {
      // jweixin SDK 未加载（极少见），直接给出可见提示而非静默空白
      setSubState({ phase: 'fail', msg: '微信 JS-SDK 未加载，请关闭页面后重新进入' });
      return () => document.removeEventListener('WeixinOpenTagsError', onOpenTagsError);
    }

    authedRequest<{ config: WxConfig; template_id: string }>(
      'GET',
      `/wx/jssdk-config?url=${encodeURIComponent(location.href.split('#')[0])}`,
    )
      .then((data) => {
        if (cancelled) return;
        if (!data?.template_id) {
          setSubState({ phase: 'fail', msg: '服务端未配置学习提醒模板' });
          return;
        }
        wx.config({
          // 访问地址带 ?wxdebug=1 时开启调试弹窗，便于真机排查 invalid signature 等配置问题
          debug: /[?&]wxdebug=1/.test(location.search),
          appId: data.config.appId,
          timestamp: data.config.timestamp,
          nonceStr: data.config.nonceStr,
          signature: data.config.signature,
          jsApiList: [],
          openTagList: ['wx-open-subscribe'],
        });
        wx.error((res: unknown) => {
          // wx.config 校验失败（签名/域名/JS安全域名等）
          console.error('[wx.config] error', res);
          const r = res as { errMsg?: string; err_code?: number };
          setSubState({ phase: 'fail', msg: `微信配置校验失败：${r?.errMsg || JSON.stringify(res)}` });
        });
        wx.ready(() => {
          if (cancelled || !subRef.current) return;
          subRef.current.innerHTML =
            `<wx-open-subscribe template="${data.template_id}" id="wx-sub-btn">` +
            '<script type="text/wxtag-template" slot="style">' +
            '<style>.wx-sub-btn{width:100%;padding:12px;border:none;border-radius:8px;' +
            'background:linear-gradient(90deg,#07c160,#10b981);color:#fff;' +
            'font-size:15px;font-weight:600;display:flex;align-items:center;justify-content:center;gap:6px;}</style>' +
            '</' + 'script>' +
            '<script type="text/wxtag-template">' +
            '<button class="wx-sub-btn">🔔 开启每周学习提醒</button>' +
            '</' + 'script>' +
            '</wx-open-subscribe>';
          setSubState({ phase: 'ready' });
          const btn = document.getElementById('wx-sub-btn');
          btn?.addEventListener('success', () => setSubscribed(true));
          btn?.addEventListener('error', (e: unknown) => {
            // 弹出/订阅授权失败：把微信返回的 errCode/errMsg 原样暴露，便于真机定位
            const detail = (e as CustomEvent<{ errCode?: number; errMsg?: string }>)?.detail;
            console.error('[wx-open-subscribe] error', e);
            alert('订阅未完成：' + (detail ? JSON.stringify(detail) : '未知错误'));
          });
        });
      })
      .catch((e) => {
        console.error('获取微信签名失败', e);
        setSubState({ phase: 'fail', msg: `获取订阅配置失败：${(e as Error)?.message || e}` });
      });

    return () => {
      cancelled = true;
      document.removeEventListener('WeixinOpenTagsError', onOpenTagsError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <div className="home-header">
        <h1>盒智家学习</h1>
        <p className="home-subtitle">
          {empCode ? `学员 ${empCode} · ` : ''}扫码答题，掌握专业技能
        </p>
      </div>

      {subscribed && (
        <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, color: '#065f46', fontSize: 13 }}>
          订阅成功！每周学习提醒将通过公众号发送给您
        </div>
      )}

      {isWx && !subscribed && subState.phase === 'loading' && (
        <div style={{ margin: '0 16px 12px', padding: '12px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 13, background: 'var(--gray-100)', borderRadius: 8 }}>
          正在加载订阅提醒…
        </div>
      )}

      {isWx && !subscribed && subState.phase === 'fail' && (
        <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
          {subState.msg}
        </div>
      )}

      {isWx && !subscribed && (
        <div ref={subRef} style={{ margin: '0 16px 12px', display: subState.phase === 'ready' ? 'block' : 'none' }} />
      )}

      <div className="scan-tip" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>
        <div style={{ margin: '0 auto 16px', width: 64, height: 64, borderRadius: 16, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="qrcode" size={32} color="var(--gray-400)" />
        </div>
        <p style={{ fontSize: 16, marginBottom: 8, color: 'var(--gray-600)' }}>扫描知识卡上的二维码开始答题</p>
        <p style={{ fontSize: 13 }}>每张卡片对应一道题，答题后立即获得专业解析</p>
        <Link to="/wrong-book" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 20, padding: '10px 20px', borderRadius: 8, background: '#4f46e5', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
          <Icon name="book" size={16} color="#fff" /> 查看错题本
        </Link>
      </div>
    </div>
  );
}

// 知识搜索功能经种子用户讨论后暂时关闭，不对学员开放（仅平台管理员在后台使用）
const NAV_ITEMS = [
  { to: '/', icon: 'home' as const, label: '首页' },
  { to: '/stats', icon: 'chart' as const, label: '统计' },
  { to: '/ranking', icon: 'trophy' as const, label: '排名' },
  { to: '/me', icon: 'user' as const, label: '我的' },
];

function NavBar() {
  const location = useLocation();
  const hideNav = ['/quiz/', '/q/', '/question/', '/auth', '/result/', '/cert'].some(p => location.pathname.startsWith(p));
  if (hideNav) return null;

  return (
    <div className="bottom-nav">
      {NAV_ITEMS.map(item => {
        const active = item.to === '/'
          ? location.pathname === '/'
          : location.pathname === item.to || location.pathname.startsWith(item.to + '/');
        return (
          <Link key={item.to} to={item.to} className={active ? 'active' : ''}>
            <Icon name={item.icon} size={20} color={active ? 'var(--color-primary)' : 'var(--gray-400)'} />
            {item.label}
          </Link>
        );
      })}
    </div>
  );
}

function SiteFooter() {
  const location = useLocation();
  // 答题/结果/证书沉浸页不显示备案，避免干扰
  const hide = ['/quiz/', '/q/', '/question/', '/result/', '/cert'].some(p => location.pathname.startsWith(p));
  if (hide) return null;
  return (
    <div className="site-footer">
      <a
        href="https://beian.mps.gov.cn/#/query/webSearch?code=42010602005097"
        rel="noreferrer"
        target="_blank"
        className="site-footer-link"
      >
        <img
          src="/beian-police.png"
          alt=""
          className="site-footer-icon"
        />
        鄂公网安备42010602005097号
      </a>
      <a
        href="https://beian.miit.gov.cn/"
        rel="noreferrer"
        target="_blank"
        className="site-footer-link"
      >
        鄂ICP备2026040019号
      </a>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const isQuizView = ['/quiz/', '/q/', '/question/', '/result/'].some(p => location.pathname.startsWith(p));
  return (
    <div className="phone">
      <div className="nav-bar">盒智家答题</div>
      <div className={`content${isQuizView ? ' content-quiz' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/q/:cardNo" element={<ProtectedRoute><SingleQuizPage /></ProtectedRoute>} />
          <Route path="/question/:publicId" element={<ProtectedRoute><SingleQuizPage /></ProtectedRoute>} />
          <Route path="/result/:attemptId" element={<ProtectedRoute><ResultPage /></ProtectedRoute>} />
          <Route path="/stats" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
          <Route path="/ranking" element={<ProtectedRoute><RankingPage /></ProtectedRoute>} />
          <Route path="/wrong-book" element={<ProtectedRoute><WrongBookPage /></ProtectedRoute>} />
          {/* 满意度调查：服务号群发链接 /survey/<id> 直达 */}
          <Route path="/survey/:id" element={<ProtectedRoute><SurveyPage /></ProtectedRoute>} />
          <Route path="/cert" element={<ProtectedRoute><CertPage /></ProtectedRoute>} />
          {/* 档案公开验真：无需登录，凭人才数字ID查询 */}
          <Route path="/cert/:code" element={<CertPage />} />
          {/* #65 知识搜索对学员关闭：不仅移除导航入口，路由也一并摘掉。
              只藏入口不摘路由的话，学员凭历史记录/收藏/直接输地址仍能进，等于没关。
              功能已迁到管理后台「知识搜索」（仅平台管理员，见 #66）。*/}
          <Route path="/me" element={<ProtectedRoute><MePage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <SiteFooter />
      <NavBar />
    </div>
  );
}
